import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import router from '@/router';

Vue.use(Vuex);

export default new Vuex.Store({
	plugins: [
		createPersistedState({
			key: 'vuex-checkout',
			paths: [
				'currency',
				'currentBalance',
				'userEmail',
				'userAccountType',
				'pages',
				'appType',
				'token',
				'gamecode',
				'displayCallbackIframe',
			],
		}),
	],
	state: {
		pages: {
			'payment-process': null,
			checkout: null,
			'checkout-success': null,
			'checkout-success-payment-only': null,
		},
		isMobile: /(iPhone|iPad|Android)/gi.test(navigator.userAgent),
		isAndroid: /(Android)/gi.test(navigator.userAgent),
		isIOS: /(iPhone|iPad|iPod)/gi.test(navigator.userAgent),
		isWebkit: /(AppleWebkit)/gi.test(navigator.userAgent),
		isFirefoxIOS: /(FxiOS)/gi.test(navigator.userAgent),
		isOperaIOS: /(OPiOS)/gi.test(navigator.userAgent),
		isChromeIOS: /(CriOS)/gi.test(navigator.userAgent),
		isMercury: /(mercury)/gi.test(navigator.userAgent),
		checkoutId: null,
		error: {},
		currentBalance: {},
		selectedValue: 'PME002',
		currency: null,
		region: environment.region,
		sandboxMode: false,
		loadingOverlayLabel: '',
		showLoadingOverlay: true,
		userEmail: '',
		userAccountType: null,
		appType: 'web',
		paymentMethodType: '',
		onetimePurchase: false,
		form: {
			errors: {},
		},
		codeRedeemResult: {},
		codeRedeemReturnUrl: null,
		channel: null,
		isShop: false,
		// payment revamp state:
		token: null,
		checkoutIdSession: null,
		accountBalance: {},
		isEUCountry: false,
		isMinor: false,
		billingLocationData: {},
		purchaseSummaryData: {},
		productsData: [],
		countryCode: null,
		zipCode: '',
		showBillingLocationFields: false,
		paymentMethodsList: null,
		selectedPg: null,
		showSelectedPg: false,
		selectedPgErrorCode: null,
		pgClicked: false, // to know if PG button was clicked or it's displaying the last used PG methods
		authorizedUrl: null,
		redirectUrl: null,
		karmakoinSelectedOption: null,
		pgLoading: false,
		gamecode: '', // steam nx purchase gamecode (ms/mb/vindictus)
		inGameOrderData: {},
		inGameTransactionResult: {},
		serviceType: '', // is used for in-game (from the fetchBillingInfoVerifyToken) for getting the payment methods by passing it to the getPaymentMethods API
		displayCallbackIframe: false,
		mileage: {
			coinName: '',
			mileageBalance: 0,
			unitName: '',
			hasFailMileagePayment: false,
		},
	},
	getters: {
		isFraudErrorCode: (state) => {
			return ['002-400-0005', '002-400-0006', '002-400-0007', '002-400-0008', '002-400-0010'].includes(
				state.selectedPgErrorCode
			);
		},
	},
	actions: {
		async fetchBillingInfoVerifyToken({ state, commit, dispatch }, params = {}) {
			try {
				if (state.productsData.length > 0) {
					return;
				}
				const response = await this._vm.$checkoutAPI.getBillingInfoVerifyToken(state.token);
				commit('setCheckoutIdSession', response.data.checkoutId);
				commit('setBillingLocationData', response.data.billingLocationInfo);
				commit('setAccountBalance', response.data.account.balance);
				commit('setIsEUCountry', response.data.account.isEUCountry);
				commit('setUserEmail', response.data.account.email);
				commit('setPurchaseSummaryData', response.data.purchaseSummary);
				commit('setProductsData', response.data.purchaseSummary.products);

				if (response.data.account && response.data.account.isMinor)
					commit('setIsMinor', response.data.account.isMinor);

				if (response.data.purchaseSummary.serviceType)
					commit('setServiceType', response.data.purchaseSummary.serviceType);

				if (state.region === 'global') {
					commit('setShowBillingLocationFields', response.data.billingLocationInfo.requireLocationSelection);

					if (response.data.billingLocationInfo.billingLocation) {
						commit('setCountryCode', response.data.billingLocationInfo.billingLocation.countryCode);
						commit('setZipCode', response.data.billingLocationInfo.billingLocation.zipCode);
					}
					if (!state.showBillingLocationFields) {
						window.dataLayer.push({
							event: `${state.appType === 'game' ? 'In Game' : 'Add Balance'} Location Submit`,
							'Returning Check': 'Automatic',
						});
						if (state.appType !== 'game') {
							window.dataLayer.push({ ecommerce: null });

							window.dataLayer.push({
								event: 'checkout',
								ecommerce: {
									checkout: {
										actionField: { step: 1 },
									},
								},
							});
						}

						await dispatch('fetchPaymentMethodsBeginTransaction');
					}
				} else {
					await dispatch('fetchPaymentMethodsBeginTransaction');
				}

				return response;
			} catch (error) {
				const errorCode = error.response && error.response.data && error.response.data.code;
				console.log(errorCode);
				if (state.region === 'tw' && state.selectedPg.status === 'maintenance') {
					router.replace({
						name: 'purchase-maintenance',
						params: {
							...(params && { ...params }),
						},
					});
				} else if (state.appType !== 'game') {
					router.replace({
						name: 'purchase-failed',
						params: {
							...(params && { ...params }),
							errorCode: errorCode ? errorCode : null,
						},
					});
				} else {
					router.replace({
						name: 'in-game-purchase-failed',
						query: {
							...(errorCode && { error_code: errorCode }),
						},
						params: {
							...(params && { ...params }),
						},
					});
				}

				error.display = 'fetchBillingInfoVerifyToken';
				throw error;
			}
		},

		async fetchPaymentMethods({ state, commit }) {
			try {
				const response = await this._vm.$checkoutAPI.getPaymentMethods(state.serviceType);
				commit('setPaymentMethodList', response.data);
				return response.data;
			} catch (error) {
				throw error;
			}
		},

		async beginPayment({ state, commit, getters }, otpCode = null) {
			try {
				if (state.isMinor && !otpCode) {
					// prompt the PIN Code verification modal for the minor account if the beginPayment doesn't have otpCode
					// and skip this piece of logic when otpCode comes from the verify pin modal, the beginPayment action will be called once pin is provided and no need to open an additional pin verification modal
					return await this._vm.$dialog.verifyParentCode();
				}
				if (!otpCode) commit('setPgLoading', true);
				commit('setSelectedPgErrorCode', null);
				commit('setAuthorizedUrl', null); // reset old authorizedUrl before getting another

				const response = await this._vm.$checkoutAPI.beginPayment(state.selectedPg.channelNo, otpCode);
				let authorizedUrl = response.data.authorizedUrl;

				if (process.env.VUE_APP_ENV === 'local') {
					// if we need to redirect on the payment from the localhost (when running npm run dev-local)
					const url = new URL(authorizedUrl);
					authorizedUrl = authorizedUrl.replace(url.origin, `https://${window.location.hostname}:8300`);
					authorizedUrl = `${authorizedUrl}&iframe=${state.selectedPg.iframe}`;
				}

				commit('setAuthorizedUrl', authorizedUrl);
				if (state.selectedPg.iframe) {
					commit('setShowSelectedPg', true);
					commit('setPgLoading', false);
				} else {
					window.location.replace(authorizedUrl);
				}
			} catch (error) {
				commit('setPgLoading', false);
				if (error.response && error.response.data && error.response.data.code) {
					commit('setSelectedPgErrorCode', error.response.data.code);
				}
				if (!getters.isFraudErrorCode || otpCode) {
					// display the fraud error messages inside the payment medhod instead of throwing error and redirec to the failed page
					// when it's minor and the fraud error code is returning inside the pin code verification we need to throw error in order to close the modal and to show the fraud messages
					throw error;
				}
			}
		},

		async fetchPaymentMethodsBeginTransaction({ commit, dispatch, state }) {
			// get payment methods list and start the most recently used PG if user has it
			try {
				const pgMethods = await dispatch('fetchPaymentMethods');
				commit('setSelectedPg', pgMethods.initiateChannel);

				// maintenance - tw
				if (state.region === 'tw' && state.selectedPg.status === 'maintenance') {
					throw new Error('TW region is under maintenance');
				}
				// end of service - th
				else if (state.region === 'th' && !pgMethods.refundable && !pgMethods.nonrefundable) {
					throw new Error('TH region is end of service');
				}

				if (pgMethods.initiateChannel && pgMethods.initiateChannel.channelNo) {
					// if user has the most recently used PG then display it (only for iframe true pg methods except TW region, it has only 1 PG method and API will return it as the most recently used PG in order to redirect next without user click)

					if (state.appType === 'game') {
						let igpAmount, igpCurrency;
						if (
							state.productsData &&
							state.productsData[0] &&
							state.productsData[0].productType === 'nxwallet'
						) {
							igpAmount = state.productsData[0].value;
							igpCurrency = 'NX';
						} else if (state.purchaseSummaryData) {
							igpAmount = state.purchaseSummaryData.subTotalAmount;
							igpCurrency = state.purchaseSummaryData.currency;
						}

						window.dataLayer.push({
							event: 'In Game Purchase PG Select',
							IGP_amount: igpAmount,
							IGP_currency: igpCurrency,
							IGP_paymentGateway: pgMethods.initiateChannel.methodName,
							IGP_autoPG: true,
							...(router.currentRoute.query.from && router.currentRoute.query.from === 'add-more-funds' // if user comes from the almost there page (TH insufficient result for truemoney/12call) and selects pg
								? { IGP_addMore: true }
								: {}),
						});
					} else {
						window.dataLayer.push({
							event: 'Add Balance Select PG',
							'Returning Check': 'Automatic',
							'Payment Gateway': pgMethods.initiateChannel.methodName,
							'PG Type': pgMethods.initiateChannel.isRefundable ? 'Credit' : 'PrePaid',
							'NX Amount': state.productsData[0].value,
						});

						window.dataLayer.push({ ecommerce: null });

						window.dataLayer.push({
							event: 'checkout',
							ecommerce: {
								currencyCode: state.productsData[0].currency,
								checkout: {
									actionField: { step: 2, option: pgMethods.initiateChannel.displayName },
									products: [
										{
											name: state.productsData[0].name,
											variant: pgMethods.initiateChannel.isRefundable ? 'Credit' : 'PrePaid',
											price: state.productsData[0].amount,
											category: 'NX',
											quantity: 1,
										},
									],
								},
							},
						});
					}

					await dispatch('beginPayment');
				}
			} catch (error) {
				throw error;
			}
		},

		async fetchVerifyEpinNexonCardsToken({ state, commit }, params = {}) {
			try {
				const response = await this._vm.$checkoutAPI.getAuthorizedUrlEpin(state.token);
				let authorizedUrl = response.data.authorizedUrl;

				// if we need to use on localhost the payment project from the localhost then replace autorizedUrl url origin otherwise just comment this if condition:
				if (process.env.VUE_APP_ENV === 'local') {
					const url = new URL(authorizedUrl);
					authorizedUrl = authorizedUrl.replace(url.origin, `https://${window.location.hostname}:8300`);
					authorizedUrl = `${authorizedUrl}&iframe=true`;
				}

				commit('setAuthorizedUrl', authorizedUrl);
				commit('setAccountBalance', response.data.account.balance);
				commit('setIsEUCountry', response.data.account.isEUCountry);
				commit('setUserEmail', response.data.account.email);
				commit('setRedirectUrl', response.data.redirectUrl);

				return response;
			} catch (error) {
				router.replace({
					name: 'purchase-failed',
					...(params
						? {
								params: {
									...params,
									errorCode:
										(error.response && error.response.data && error.response.data.code) ||
										'invalid-token',
								},
						  }
						: {}),
				});
				throw error;
			}
		},

		async fetchVerifySteamToken({ state, commit }) {
			try {
				let serviceType = 'steam';
				// verify token and get billing info for taking the checkoutId
				const billing = await this._vm.$checkoutAPI.getBillingInfoVerifyToken(state.token, serviceType);
				commit('setCheckoutIdSession', billing.data.checkoutId);

				// get the payment method with the checkoutId (need the channelNo for getting the autorizedUrl)
				const paymentMethod = await this._vm.$checkoutAPI.getPaymentMethods(serviceType);

				// get authorizedUrl with the chanelNo from the steam payment method
				const response = await this._vm.$checkoutAPI.beginPayment(paymentMethod.data.refundable[0].channelNo);

				let authorizedUrl = response.data.authorizedUrl;

				if (process.env.VUE_APP_ENV === 'local') {
					// if we need to redirect on the payment from the localhost (when running npm run dev-local)
					const url = new URL(authorizedUrl);
					authorizedUrl = authorizedUrl.replace(url.origin, `https://${window.location.hostname}:8300`);
				}

				const url = new URL(authorizedUrl);
				url.searchParams.set('gamecode', state.gamecode);

				window.location.replace(url);

				return url;
			} catch (error) {
				throw error;
			}
		},
		async fetchVerifyInGameToken({ commit }, params = {}) {
			try {
				const order = await this._vm.$checkoutAPI.verifyInGameToken(params.token, params.serviceType);
				commit('setAccountBalance', order.data.account.balance);
				commit('setCheckoutIdSession', order.data.checkoutId);
				commit('setIsMinor', order.data.account.isMinor);
				commit('setUserEmail', order.data.account.email);
				commit('setInGameOrderData', {
					...order.data,
				});
				return order;
			} catch (error) {
				throw error;
			}
		},
		async fetchMileageBalance({ commit }) {
			try {
				const res = await this._vm.$checkoutAPI.getMileageBalance();
				commit('setMileageInfo', res.data);
				return res;
			} catch (error) {
				throw error;
			}
		},
	},

	mutations: {
		setChannel(state, payload) {
			state.channel = payload;
		},
		setCodeRedeemReturnUrl(state, payload) {
			state.codeRedeemReturnUrl = payload;
		},
		setCodeRedeemResult(state, payload) {
			state.codeRedeemResult = { ...payload };
		},
		setAppType(state, payload) {
			state.appType = payload;
		},
		setIsMinor(state, payload) {
			state.isMinor = payload;
		},
		setPaymentMethodType(state, payload) {
			state.paymentMethodType = payload;
		},
		setUserEmail(state, payload) {
			state.userEmail = payload;
		},
		setUserAccountType(state, payload) {
			state.userAccountType = payload;
		},
		setCheckoutId(state, payload) {
			state.checkoutId = payload;
		},
		setCurrentBalance(state, payload) {
			state.currentBalance = Object.assign(state.currentBalance, payload);
		},
		setCurrency(state, payload) {
			state.currency = payload;
		},
		enableSandboxMode(state) {
			state.sandboxMode = true;
		},
		startLoadingOverlay(state, payload) {
			state.loadingOverlayLabel = payload;
			state.showLoadingOverlay = true;
		},
		endLoadingOverlay(state) {
			state.showLoadingOverlay = false;
		},
		setFormError(state, payload) {
			state.form.errors = { ...state.form.errors, code: payload.field, msg: payload.msgs };
		},
		setShop(state) {
			state.isShop = true;
		},
		// payment revamp:
		setToken(state, payload) {
			state.token = payload;
		},
		setCheckoutIdSession(state, payload) {
			state.checkoutIdSession = payload;
		},
		setBillingLocationData(state, payload) {
			state.billingLocationData = payload;
		},
		setAccountBalance(state, payload) {
			state.accountBalance = Object.assign(state.accountBalance, payload);
		},
		setIsEUCountry(state, payload) {
			state.isEUCountry = payload;
		},
		setPurchaseSummaryData(state, payload) {
			state.purchaseSummaryData = payload;
		},
		setServiceType(state, payload) {
			state.serviceType = payload;
		},
		setProductsData(state, payload) {
			state.productsData = payload;
		},
		setCountryCode(state, payload) {
			state.countryCode = payload;
		},
		setZipCode(state, payload) {
			state.zipCode = payload;
		},
		setShowBillingLocationFields(state, payload) {
			state.showBillingLocationFields = payload;
		},
		setPaymentMethodList(state, payload) {
			state.paymentMethodsList = payload;
		},
		setSelectedPg(state, payload) {
			state.selectedPg = payload;
		},
		setShowSelectedPg(state, payload) {
			state.showSelectedPg = payload;
		},
		setPgClicked(state, payload) {
			state.pgClicked = payload;
		},
		setSelectedPgErrorCode(state, payload) {
			state.selectedPgErrorCode = payload;
		},
		setAuthorizedUrl(state, payload) {
			state.authorizedUrl = payload;
		},
		setRedirectUrl(state, payload) {
			state.redirectUrl = payload;
		},
		setKarmakoinSelectedOption(state, payload) {
			state.karmakoinSelectedOption = payload;
		},
		setPgLoading(state, payload) {
			state.pgLoading = payload;
		},
		setGameCode(state, payload) {
			state.gamecode = payload;
		},
		setInGameOrderData(state, payload) {
			state.inGameOrderData = payload;
		},
		setInGameTransactionResult(state, payload) {
			state.inGameTransactionResult = payload;
		},
		setDisplayCallbackIframe(state, payload) {
			state.displayCallbackIframe = payload;
		},
		setMileageInfo(state, payload) {
			state.mileage = payload;
		},
	},
});
